/**
 * Allegiant Sonic Logo — First-Auth Micro Implementation
 *
 * Plays the approved Pulse Seal Classic app audio asset exactly once per
 * browser/app installation after the first successful authenticated entry into the EMR.
 *
 * Guaranteed constraints:
 * - Does NOT play on every login, page refresh, session restore, token refresh,
 *   provider switch, or normal navigation.
 * - Tracks local state via localStorage key 'allegiant_sonic_intro_played'.
 * - If autoplay is blocked, fails silently without blocking authentication or showing errors.
 * - Supports one deferred attempt on the first post-auth user gesture, then marks handled.
 */

import { playPulseSeal } from './pulseAudio';

export const SONIC_INTRO_STORAGE_KEY = 'allegiant_sonic_intro_played';
export const SONIC_AUDIO_ASSET_PATH = '/pulse-seal.wav';

/**
 * Checks if the authenticating user is Justin / Owner.
 */
export function isJustinUser(user?: { email?: string; id?: string; role?: string; name?: string } | null): boolean {
  if (!user) return false;
  const email = (user.email || '').toLowerCase().trim();
  const id = (user.id || '').toUpperCase().trim();
  const name = (user.name || '').toLowerCase().trim();
  return (
    email === 'justin@allegianttherapy.com' ||
    email === 'jsegura@allegianttherapy.com' ||
    email === 'billing@allegianttherapy.com' ||
    email.includes('justin') ||
    name.includes('justin') ||
    id === 'USR-JUSTIN' ||
    id === 'PROV-JUSTIN' ||
    user.role === 'OWNER'
  );
}

/**
 * Triggers the authoritative Pulse audio seal after a successful explicit authenticated entry from the login gate.
 *
 * Operational rules:
 * - Plays the authoritative Pulse seal after every explicit successful interactive login.
 * - Does NOT play on page refresh, session restore, token refresh, or background re-renders.
 * - Safe handling for autoplay; fails silently without blocking login.
 */
export function triggerFirstAuthSonicLogo(user?: { email?: string; id?: string; role?: string } | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (typeof window.localStorage !== 'undefined') {
      localStorage.setItem(SONIC_INTRO_STORAGE_KEY, 'true');
    }

    // Play authoritative Pulse seal with debouncing
    playPulseSeal({ cooldownMs: 1000 });
  } catch {
    // Fail silently: never throw, never show errors, never block authentication
  }
}

/**
 * Ensures existing restored sessions on page mount do not replay the sonic logo.
 */
export function ensureSonicIntroHandledOnSessionRestore(): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    if (!localStorage.getItem(SONIC_INTRO_STORAGE_KEY)) {
      localStorage.setItem(SONIC_INTRO_STORAGE_KEY, 'true');
    }
  } catch {
    // Fail silently
  }
}
